import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useGetIdentity } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import type { CommissionsScope, PeriodKey } from '../types';
import { useWidgetData } from '../useWidgetData';
import { formatCurrency } from '../utils';
import { LABEL_COLOR } from '../constants';
import { WidgetCard } from './WidgetCard';
import { WidgetScopeToggle } from './WidgetScopeToggle';

interface CommissionsWidgetProps {
    period: PeriodKey;
    enabled: boolean;
}

export const CommissionsWidget = ({ period, enabled }: CommissionsWidgetProps) => {
    const navigate = useNavigate();
    const { identity } = useGetIdentity();
    const isSuperuser = Boolean((identity as { administrator?: boolean } | undefined)?.administrator);
    const [scope, setScope] = useState<CommissionsScope>('all');
    const { data, isLoading, error } = useWidgetData('commissions', period, enabled, { scope });

    // Superusers switch between everyone (per user) and their own per-project view.
    const scopeToggle = isSuperuser ? (
        <WidgetScopeToggle value={scope} onChange={setScope} />
    ) : undefined;

    if (error) {
        return (
            <WidgetCard
                title="Pending Commissions"
                subtitle="By contractor and project"
                action={scopeToggle}
                children={
                    <Typography variant="body2" sx={{ color: '#EF4444' }}>
                        Failed to load commissions widget
                    </Typography>
                }
            />
        );
    }

    if (isLoading || !data) {
        return (
            <WidgetCard
                title="Pending Commissions"
                subtitle="By contractor and project"
                action={scopeToggle}
                loading
            />
        );
    }

    // Superusers get per-user totals (by_user); everyone else gets per-project rows.
    const userRows = data.by_user ?? null;
    const hasRows = userRows ? userRows.length > 0 : data.rows.length > 0;

    return (
        <WidgetCard
            title="Pending Commissions"
            subtitle={userRows ? 'By User' : 'By Contractor and Project'}
            action={scopeToggle}
            headline={formatCurrency(data.total_commission)}
        >
            {!hasRows ? (
                <Typography variant="body2" sx={{ color: LABEL_COLOR }}>
                    No data for this period
                </Typography>
            ) : userRows ? (
                <Stack spacing={1.5}>
                    {userRows.map((row) => (
                        <Box
                            key={row.user_id}
                            sx={{
                                borderRadius: '8px',
                                backgroundColor: '#F9FAFB',
                                p: 1.5,
                            }}
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                spacing={1}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500, color: '#111827', mb: 0.25 }}
                                    >
                                        {row.user_name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: LABEL_COLOR }}>
                                        {row.quote_count} {row.quote_count === 1 ? 'quote' : 'quotes'}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}
                                >
                                    {formatCurrency(row.commission_total)}
                                </Typography>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            ) : (
                <Stack spacing={1.5}>
                    {data.rows.map((row) => (
                        <Box
                            key={`${row.contractor_id}-${row.project_id}`}
                            sx={{
                                borderRadius: '8px',
                                backgroundColor: '#F9FAFB',
                                p: 1.5,
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                '&:hover': {
                                    backgroundColor: '#F3F4F6',
                                },
                            }}
                            onClick={() => navigate(`/projects/${row.project_id}/show`)}
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                spacing={1}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500, color: '#111827', mb: 0.25 }}
                                    >
                                        {row.project_no != null ? `${row.project_no} - ${row.address}` : row.address}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: LABEL_COLOR }}>
                                        {row.contractor_name}
                                    </Typography>
                                </Box>
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}
                                >
                                    {formatCurrency(row.commission_total)}
                                </Typography>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}
        </WidgetCard>
    );
};

CommissionsWidget.displayName = 'CommissionsWidget';
